import type { VenueReservationSettingHistoryItem, VenueReservationSettingView } from './reservationSettingTypes'

/**
 * 예약 기능 설정 화면 순수 함수(#758 설계 §2 "UI 흐름"). 화면은 이 파일이 만든 표시값만 그린다 —
 * 서버 판정(`canEnable`)을 다시 계산하지 않는다(#757 §1 "canEnable 을 화면이 다시 계산하지 말 것").
 */

/**
 * 조건 식별자 → 이동 경로(`VenueLayout` 하위 상대 경로). 아는 값만 매핑한다 — forward-only 라
 * 모르는 값에 임의 경로를 붙이면 잘못된 화면으로 보낼 위험이 있다(#758 자율 결정 4).
 *
 * 🚨 `OPERATION_VERIFIED` 는 전용 신청 화면이 아직 없어 가장 가까운 기존 화면(`venue`, "구장
 * 정보·코트 설정" 요약)으로 보낸다 — 실제 신청 플로우가 생기면 이 한 줄만 바꾸면 된다(#758 자율
 * 결정 3).
 */
const CONDITION_TARGET_PATH: Record<string, string> = {
  OPERATION_VERIFIED: 'venue',
  COURT_AND_FEE: 'courts',
  OPERATING_HOURS_AND_UNIT: 'courts',
}

export function reservationConditionTargetPath(condition: string): string | null {
  return CONDITION_TARGET_PATH[condition] ?? null
}

/** Switch 비활성 판정 — 켜져 있으면 항상 끌 수 있고, 꺼져 있고 조건 미충족이면 켤 수 없다. */
export function isReservationSwitchDisabled(view: Pick<VenueReservationSettingView, 'reservationEnabled' | 'canEnable'>): boolean {
  return !view.reservationEnabled && !view.canEnable
}

/** 상단 카드 부제 — 이슈 "ON 상태 안내" 문구를 그대로 쓴다. */
export function reservationStatusSubtitle(view: Pick<VenueReservationSettingView, 'reservationEnabled' | 'canEnable'>): string {
  if (view.reservationEnabled) return '사용 중 · 예약·결제·취소·환불·정산을 사용해요'
  return view.canEnable ? '켤 수 있어요' : '조건을 채우면 켤 수 있어요'
}

/**
 * 미충족 배너 고정 문구. Figma 카피는 그 시점 미충족 조건 중 일부만 요약한 것으로 보이나, 서버
 * 계약에 그런 요약 필드가 없어 조건이 늘어나면 문장이 깨질 위험이 있다 — 고정 문구로 대체한다
 * (#758 자율 결정 5).
 */
export const RESERVATION_UNMET_BANNER_TEXT = '아래 활성화 조건을 모두 채우면 예약 기능을 켤 수 있어요'

/** 하단 고정 안내 — Figma 문구 그대로, ON/OFF 상태와 무관하게 항상 노출한다. */
export const RESERVATION_FOOTER_NOTICE =
  '예약 기능을 꺼도 이미 확정된 예약은 유지돼요. 인증 미완료 구장은 켜도 노출되지 않고 신규 예약을 받을 수 없어요.'

/** OFF 확인 모달 안내 4줄 — 이슈 원문 그대로(설계 §2 "UI 흐름"). */
export const RESERVATION_DISABLE_GUIDE_LINES: string[] = [
  '신규 온라인 예약 접수가 중단돼요.',
  '사용자 검색·상세에서 예약 버튼이 비활성화돼요.',
  '이미 확정된 예약·정산·환불은 정상 처리가 계속돼요.',
  '전화·현장 예약 등록은 계속 사용할 수 있어요.',
]

/**
 * 수수료·정산 안내 카드 상단 고정 배지 — "이용권 없이도 예약 기능을 켤 수 있다"는 안내(이슈 #796
 * 요구사항 2). 이용권 폐지(#762) 이후에도 별도 이용권 구매·구독 결제가 없다는 사실을 명시적으로
 * 안내하기 위해 ON/조건 미충족 상태와 무관하게 항상 노출한다(Figma `14039-40075`·`14039-40192` 공통).
 */
export const RESERVATION_FEE_FREE_NOTICE = '이용권 없이 무료로 켤 수 있어요'

/** 수수료·정산 안내 카드 — API 범위 밖(정적 카피, Figma 고정). */
export const RESERVATION_FEE_SETTLEMENT_FACTS: { label: string; value: string }[] = [
  { label: '예약 결제', value: '콕 지갑 전용' },
  { label: '예약 수수료', value: '결제 시점 수수료율 적용' },
  { label: '정산', value: '매월 말 확정 · 다음 달 10일 지급' },
]

/** 이력 1행의 전환 텍스트 — "OFF → ON" / "ON → OFF". */
export function reservationHistoryTransitionText(
  item: Pick<VenueReservationSettingHistoryItem, 'beforeEnabled' | 'afterEnabled'>
): string {
  const label = (enabled: boolean) => (enabled ? 'ON' : 'OFF')
  return `${label(item.beforeEnabled)} → ${label(item.afterEnabled)}`
}
